import { ExtractionStatus, ReceiptSource } from "../../../models";

/**
 * Maps extraction confidence to a confidence band.
 * @param confidence - The confidence value (0–1) or null
 * @returns "high" (≥0.80), "med" (≥0.50), "low" (<0.50), or "none" (null)
 */
export function confidenceBand(
  confidence: number | null
): "high" | "med" | "low" | "none" {
  if (confidence === null) return "none";
  if (confidence >= 0.8) return "high";
  if (confidence >= 0.5) return "med";
  return "low";
}

/**
 * Formats a number as USD currency string.
 * @param amount - The amount in dollars or null
 * @returns Formatted string like "$188.77" or "—" for null
 */
export function formatMoney(amount: number | null): string {
  if (amount === null) return "—";
  return `$${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Formats a weight value as a string.
 * @param weight - The weight value or null
 * @returns The weight as a string or "—" for null
 */
export function formatWeight(weight: number | null): string {
  if (weight === null) return "—";
  return weight.toString();
}

/**
 * Returns a CSS class string for the status pill.
 * Uses the project-convention "status-pill <modifier>" pattern (custom SCSS, no Tailwind).
 * @param status - The extraction status
 * @returns CSS class string for styling
 */
export function statusPillClass(status: ExtractionStatus): string {
  return `status-pill ${status}`;
}

/**
 * Formats an order's dumpster_size enum for display.
 * @param size - The dumpster size ("10" / "15" / "clean_fill"; "" if unattributed)
 * @returns "10 yd" / "15 yd" / "Clean fill", or "" when unknown
 */
export function dumpsterSizeLabel(size: string): string {
  if (!size) return "";
  if (size === "clean_fill") return "Clean fill";
  return `${size} yd`;
}

/**
 * Returns a human-readable label for a receipt source.
 * @param source - The receipt source type
 * @returns Human-readable label
 */
export function receiptTypeLabel(source: ReceiptSource): string {
  const labels: Record<ReceiptSource, string> = {
    photo: "Photo",
    pending: "Pending",
    email: "Email",
    manual: "Manual",
  };
  return labels[source];
}
